"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Routes, Route } from "react-router-dom"
import Layout from "./components/Layout"
import Home from "./pages/Home"
import About from "./pages/About"
import ThumbnailDownloader from "./pages/ThumbnailDownloader"
import TitleDescriptionExtractor from "./pages/TitleDescriptionExtractor"
import EmbedCodeGenerator from "./pages/EmbedCodeGenerator"
import TimestampLinkGenerator from "./pages/TimestampLinkGenerator"
import TranscriptChat from "./pages/TranscriptChat"
import { extractYoutubeVideoId } from "./utils/youtubeUtils"
import { AuthProvider } from "./contexts/AuthContext"
import AuthModal from "./components/AuthModal"
import { useAuth } from "./contexts/AuthContext"
import AuthCallback from "./pages/AuthCallback"

function AppContent() {
  const [url, setUrl] = useState("")
  const [videoId, setVideoId] = useState<string | null>(null)
  const [error, setError] = useState("")
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode")
    return saved ? JSON.parse(saved) : false
  })
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const { user } = useAuth()

  useEffect(() => {
    console.log("App component mounted")
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputUrl = e.target.value
    setUrl(inputUrl)

    if (!inputUrl) {
      setError("")
      setVideoId(null)
      return
    }

    const extractedId = extractYoutubeVideoId(inputUrl)
    if (extractedId) {
      setError("")
      setVideoId(extractedId)
    } else {
      setError("Invalid YouTube URL. Please enter a valid YouTube video URL.")
      setVideoId(null)
    }
  }

  const handleVideoChange = (newUrl: string) => {
    setUrl(newUrl)
    const newVideoId = extractYoutubeVideoId(newUrl)
    if (newVideoId) {
      setVideoId(newVideoId)
      setError("")
    }
  }

  try {
    return (
      <div
        className={`min-h-screen transition-colors duration-200 ${darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"}`}
      >
        <Layout 
          darkMode={darkMode} 
          setDarkMode={setDarkMode}
          onVideoSelect={(videoId, title) => {
            handleVideoChange(`https://www.youtube.com/watch?v=${videoId}`);
          }}
          onAuthClick={() => setIsAuthModalOpen(true)}
          user={user}
        >
          <div className="container mx-auto px-4 py-6">
            <Routes>
              <Route
                path="/"
                element={
                  <Home
                    url={url}
                    videoId={videoId}
                    error={error}
                    darkMode={darkMode}
                    handleInputChange={handleInputChange}
                    onVideoChange={handleVideoChange}
                  />
                }
              />
              <Route path="/about" element={<About darkMode={darkMode} />} />
              <Route path="/thumbnail-downloader" element={<ThumbnailDownloader />} />
              <Route path="/title-description-extractor" element={<TitleDescriptionExtractor />} />
              <Route path="/embed-code-generator" element={<EmbedCodeGenerator />} />
              <Route path="/timestamp-link-generator" element={<TimestampLinkGenerator />} />
              <Route path="/transcript-chat" element={<TranscriptChat videoUrl={url} initialTranscript="" initialVideoTitle="" />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
            </Routes>
          </div>
        </Layout>

        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
        />
      </div>
    )
  } catch (error) {
    console.error("Error in App component:", error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Something went wrong</h1>
          <p className="text-gray-600 dark:text-gray-300">Please try refreshing the page</p>
        </div>
      </div>
    )
  }
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}
